import React from "react";

import { makeStyles } from "tss-react/mui";
import { ButtonBase, Box, Typography, Grid } from "@mui/material";

import avatarPlaceholder from "../libComponents/avatar.png";
import { AddAPhotoOutlined } from "@mui/icons-material";

const useStyles = makeStyles()((theme) => ({
  buttonBase: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 0,
    gap: theme.spacing(1),
  },
  avatarButton: {
    position: "relative",
    display: "inline-flex",
    width: "20vw",
    maxWidth: "120px",
    aspectRatio: "1",
    borderRadius: "50%",
    overflow: "hidden",
    padding: 0,
  },
  avatarContainer: {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.grey[200],
    backgroundSize: "cover",
    backgroundPosition: "center",

    "& > input": {
      visibility: "hidden",
      height: 0,
    },
  },
  avatarImage: {
    position: "absolute",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
}));

const getImgData = (
  otherProps: AvatarUploadFileProps | AvatarUploadDataProps
): [string, string] => {
  let name = "";
  let src = "";
  if ("currentFile" in otherProps && otherProps.currentFile?.length) {
    name = otherProps.currentFile[0].name;
    src = URL.createObjectURL(otherProps.currentFile[0]);
  } else if ("fileName" in otherProps) {
    name = otherProps.fileName || "";
    src = otherProps.fileSrc || "";
  }
  return [name, src];
};

export const AvatarUpload = ({
  onFileUpload,
  ...otherProps
}: AvatarUploadProps) => {
  const { classes } = useStyles();

  const [, fileSrc] = getImgData(otherProps);
  const displaySrc = fileSrc || avatarPlaceholder;

  const handleFileUpload = ({ target }) => {
    const file = target.files.length ? target.files : null;
    if (file) {
      onFileUpload(file);
    }
  };

  return (
    <ButtonBase component="label" className={classes.buttonBase}>
      <input
        type="file"
        onChange={handleFileUpload}
        accept="image/*"
        style={{ visibility: "hidden", height: 0 }}
      />
      <Box className={classes.avatarButton}>
        <Box className={classes.avatarContainer}>
          <img src={displaySrc} alt="avatar" className={classes.avatarImage} />
        </Box>
      </Box>
      <Grid item container alignItems="center" justifyContent="center">
        <AddAPhotoOutlined sx={{ mr: 1 }} color="secondary" />
        <Typography
          variant="caption"
          color="secondary"
          sx={{ textDecoration: "underline" }}
        >
          Editar foto
        </Typography>
      </Grid>
    </ButtonBase>
  );
};

export type AvatarUploadProps = BaseAvatarUploadProps &
  (AvatarUploadFileProps | AvatarUploadDataProps);

type BaseAvatarUploadProps = {
  onFileUpload: (file: HTMLInputElement["files"]) => void;
};

/** Mostly for "create" forms, where a new file is being uploaded */
type AvatarUploadFileProps = {
  currentFile?: HTMLInputElement["files"];
};

/** Mostly for "edit" forms, where an existing file is being displayed and possibly updated */
type AvatarUploadDataProps = {
  fileName?: string;
  fileSrc?: string;
};
